import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Select, Tooltip } from 'flowbite-react';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, Loader2 } from 'lucide-react';
// ... rest of imports unchanged ...
import { Drawer } from '../../../components/Drawer';
import Input from '../../../components/input';
import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import { toast } from 'react-toastify';
import { extractAxiosErrorMessage } from '../../../utils/helpers';
import { BsInfoCircle } from 'react-icons/bs';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import StackDetailsModal from './stackDetailsModal';
import AutoscaleBillingNotice from '../../../components/pricingModelModal';
import { useDispatch } from 'react-redux';
import { getSecrets } from '../../../hooks/local/userReducer';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const defaultValues = {
  image_id: '',
  flavor: '',
  network_id: '', // No default network
  subnet_id: '',
  key_name: '',
  vn_name: '',
  security_groups: [],
  app_port: '',
  asg_desired_capacity: 1,
  asg_min_size: 1,
  asg_max_size: 2,
  asg_cooldown: 60,
  scale_up_adjustment: 1,
  scale_down_adjustment: 1,
  scaling_cooldown: 60,
  cpu_scale_up_threshold_percent: 1,
  cpu_scale_down_threshold_percent: 1,
  cpu_granularity: 300,
  cpu_evaluation_periods: 1,
  ram_scale_up_threshold_percent: 1,
  ram_scale_down_threshold_percent: 1,
  ram_granularity: 300,
  ram_evaluation_periods: 1,
  lb_protocol: '',
  lb_protocol_port: 1,
  lb_algorithm: '',
  health_check_type: '',
  health_check_delay: 60,
  health_check_timeout: 0,
  health_check_max_retries: 1,
  health_check_url_path: '/',
  health_check_expected_codes: '200',
  default_tls_container_ref: '',
  pool_protocol: 'HTTP',
};

const AutoScaling = () => {
  document.title = 'Autoscaling - Cloudvantage';

  const dispatch = useDispatch();
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [subnets, setSubNets] = useState([]);
  const [keypairs, setKeyPairs] = useState([]);
  const [securityGroups, setSecurityGroups] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [stacks, setStacks] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRefs = useRef({});
  const [target, setTarget] = useState('');
  const [detailsTarget, setDetailsTarget] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [stackPayload, setStackPayload] = useState({});
  const [isfetchingDetails, setIsFetchingDetails] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(true);
  const [selectedFlavorDetails, setSelectedFlavorDetails] = useState(null);
  const [showFlavorModal, setShowFlavorModal] = useState(false);
  const [filteredSubnets, setFilteredSubnets] = useState([]);
  const [securityGroupsDropdownOpen, setSecurityGroupsDropdownOpen] =
    useState(false);
  const [securityGroupsSearch, setSecurityGroupsSearch] = useState('');
  const securityGroupsDropdownRef = useRef(null);
  const subnetIdRef = useRef(null);
  const vnNameRef = useRef(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingStackId, setEditingStackId] = useState(null);
  const [editingStackName, setEditingStackName] = useState(null);

  // Formik form setup
  const stackForm = useFormik({
    initialValues: defaultValues,
    validationSchema: Yup.lazy(() =>
      Yup.object({
        // Conditionally validate image_id, flavor, and security_groups based on edit mode
        image_id: !isEditMode
          ? Yup.string().required('Image is required')
          : Yup.string().notRequired(),
        flavor: !isEditMode
          ? Yup.string().required('Flavor is required')
          : Yup.string().notRequired(),
        security_groups: !isEditMode
          ? Yup.array()
              .min(1, 'At least one security group is required')
              .required('At least one security group is required')
          : Yup.array().notRequired(),
        key_name: !isEditMode
          ? Yup.string().required('Key pair is required')
          : Yup.string().notRequired(),
        network_id: Yup.string().required('Network is required'),
        subnet_id: Yup.string().required('Subnet is required'),
        vn_name: !isEditMode
          ? Yup.string()
              .required('VM Name is required')
              .trim()
              .min(1, 'VM Name cannot be empty')
          : Yup.string().notRequired(),
        app_port: Yup.number()
          .required('App Port is required')
          .min(1, 'App Port must be 1 or greater')
          .positive('App Port must be greater than 0'),
        asg_min_size: Yup.number()
          .min(0, 'ASG Min Size cannot be negative')
          .required('ASG Min Size is required'),
        asg_max_size: Yup.number()
          .min(1, 'ASG Max Size must be greater than 0')
          .required('ASG Max Size is required')
          .test(
            'min-max',
            'ASG Max Size must be greater than or equal to Min Size',
            function (value) {
              const min = this.parent.asg_min_size;
              return !min || !value || value >= min;
            },
          ),
        asg_desired_capacity: Yup.number()
          .min(0, 'ASG Desired Capacity cannot be negative')
          .required('ASG Desired Capacity is required')
          .test(
            'desired-range',
            'ASG Desired Capacity must be between Min and Max Size',
            function (value) {
              const min = this.parent.asg_min_size;
              const max = this.parent.asg_max_size;
              if (!min || !max) return true;
              return value >= min && value <= max;
            },
          )
          .test(
            'desired-min',
            'ASG Desired Capacity is required when Min Size is set',
            function (value) {
              const min = this.parent.asg_min_size;
              return !min || value > 0;
            },
          ),
        asg_cooldown: Yup.number()
          .min(0, 'ASG Cooldown cannot be negative')
          .required('ASG Cooldown is required'),
        scale_up_adjustment: Yup.number()
          .min(0, 'Scale Up Adjustment cannot be negative')
          .required('Scale Up Adjustment is required'),
        scale_down_adjustment: Yup.number()
          .min(0, 'Scale Down Adjustment cannot be negative')
          .required('Scale Down Adjustment is required'),
        scaling_cooldown: Yup.number()
          .min(0, 'Scaling Cooldown cannot be negative')
          .required('Scaling Cooldown is required'),
        cpu_scale_up_threshold_percent: Yup.number()
          .min(1, 'CPU Scale Up Threshold must be between 1 and 100')
          .max(100, 'CPU Scale Up Threshold must be between 1 and 100')
          .required('CPU Scale Up Threshold is required'),
        cpu_scale_down_threshold_percent: Yup.number()
          .min(1, 'CPU Scale Down Threshold must be between 1 and 100')
          .max(100, 'CPU Scale Down Threshold must be between 1 and 100')
          .required('CPU Scale Down Threshold is required')
          .test(
            'cpu-down-up',
            'CPU Scale Down Threshold must be less than Scale Up Threshold',
            function (value) {
              const up = this.parent.cpu_scale_up_threshold_percent;
              return !up || !value || value < up;
            },
          ),
        cpu_evaluation_periods: Yup.number()
          .min(1, 'CPU Evaluation Periods must be 1 or greater')
          .required('CPU Evaluation Periods is required'),
        ram_scale_up_threshold_percent: Yup.number()
          .min(1, 'RAM Scale Up Threshold must be between 1 and 100')
          .max(100, 'RAM Scale Up Threshold must be between 1 and 100')
          .required('RAM Scale Up Threshold is required'),
        ram_scale_down_threshold_percent: Yup.number()
          .min(1, 'RAM Scale Down Threshold must be between 1 and 100')
          .max(100, 'RAM Scale Down Threshold must be between 1 and 100')
          .required('RAM Scale Down Threshold is required')
          .test(
            'ram-down-up',
            'RAM Scale Down Threshold must be less than Scale Up Threshold',
            function (value) {
              const up = this.parent.ram_scale_up_threshold_percent;
              return !up || !value || value < up;
            },
          ),
        ram_evaluation_periods: Yup.number()
          .min(1, 'RAM Evaluation Periods must be 1 or greater')
          .required('RAM Evaluation Periods is required'),
        lb_protocol: Yup.string().required('Listener Protocol is required'),
        lb_protocol_port: Yup.number()
          .when('lb_protocol', {
            is: 'TCP',
            then: (schema) =>
              schema
                .required('LB Protocol Port is required for TCP protocol')
                .min(1, 'LB Protocol Port must be 1 or greater'),
            otherwise: (schema) =>
              schema.min(0, 'LB Protocol Port cannot be negative'),
          })
          .min(1, 'LB Protocol Port must be 1 or greater'),
        lb_algorithm: Yup.string().required(
          'Load Balancer Algorithm is required',
        ),
        health_check_type: Yup.string().required(
          'Health Check Type is required',
        ),
        health_check_delay: Yup.number().min(
          0,
          'Health Check Delay cannot be negative',
        ),
        health_check_timeout: Yup.number().min(
          0,
          'Health Check Timeout cannot be negative',
        ),
        health_check_max_retries: Yup.number()
          .min(1, 'Health Check Max Retries must be between 1 and 10')
          .max(10, 'Health Check Max Retries must be between 1 and 10')
          .required('Health Check Max Retries is required'),
        health_check_url_path: Yup.string(),
        health_check_expected_codes: Yup.string(),
        default_tls_container_ref: Yup.string().when('lb_protocol', {
          is: (val) => val === 'TERMINATED_HTTPS',
          then: (schema) =>
            schema.required(
              'SSL Certificate is required for TERMINATED_HTTPS protocol',
            ),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    ),
    onSubmit: async (values, { setSubmitting }) => {
      await handleCreateStack(values, setSubmitting);
    },
    enableReinitialize: true,
  });

  // Prevent negative numbers and values > 100 for percentage fields
  const handleKeyDown = (e) => {
    const percentageFields = [
      'cpu_scale_up_threshold_percent',
      'cpu_scale_down_threshold_percent',
      'ram_scale_up_threshold_percent',
      'ram_scale_down_threshold_percent',
    ];

    if (e.target.type === 'number') {
      // Prevent minus key, plus key (for number input), and 'e' key (scientific notation) for all numeric fields
      if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
        e.preventDefault();
      }

      // For percentage fields, prevent typing digits that would exceed 100
      if (percentageFields.includes(e.target.name)) {
        // Allow backspace, delete, arrow keys, tab, etc.
        if (
          e.key === 'Backspace' ||
          e.key === 'Delete' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown' ||
          e.key === 'Tab' ||
          e.key === 'Enter' ||
          e.ctrlKey ||
          e.metaKey
        ) {
          return;
        }

        const currentValue = e.target.value || '';
        const selectionStart = e.target.selectionStart || 0;
        const selectionEnd = e.target.selectionEnd || 0;
        const beforeSelection = currentValue.substring(0, selectionStart);
        const afterSelection = currentValue.substring(selectionEnd);
        const newChar = e.key;

        // If typing a digit
        if (/[0-9]/.test(newChar)) {
          const potentialValue = beforeSelection + newChar + afterSelection;
          const numValue = Number(potentialValue);

          // Prevent if the value would exceed 100 or if it's a 3+ digit number starting with 1
          if (
            numValue > 100 ||
            (potentialValue.length >= 3 &&
              potentialValue[0] === '1' &&
              potentialValue[1] !== '0')
          ) {
            e.preventDefault();
          }
        } else if (newChar.length === 1 && !/[0-9]/.test(newChar)) {
          // Prevent any non-digit characters except control keys
          e.preventDefault();
        }
      }
    }
  };
  // Scroll to first error when form submission fails
  useEffect(() => {
    if (stackForm.submitCount > 0 && !stackForm.isValid) {
      const firstErrorKey = Object.keys(stackForm.errors)[0];
      if (firstErrorKey) {
        const element = document.getElementsByName(firstErrorKey)[0];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // If it's a focusable element, focus it too
          if (typeof element.focus === 'function') {
            element.focus();
          }
        }
      }
    }
  }, [stackForm.submitCount, stackForm.isValid, stackForm.errors]);

  const handleChange = (e) => {
    const { name, type, value, checked, multiple, selectedOptions, options } =
      e.target;

    if (multiple && !selectedOptions) {
      // Fallback if the environment doesn't expose selectedOptions consistently
      const selected = Array.from(options)
        .filter((o) => o.selected)
        .map((o) => o.value);
      stackForm.setFieldValue(name, selected);
      return;
    }

    if (multiple) {
      // If you specifically want toggle-by-click behavior:
      const clicked = value; // works when browser sets target.value to the last interacted option
      const current = Array.isArray(stackForm.values[name])
        ? stackForm.values[name]
        : [];
      const has = current.includes(clicked);
      const next = has
        ? current.filter((v) => v !== clicked)
        : [...current, clicked];
      stackForm.setFieldValue(name, next);
      return;
    }

    const nextValue = type === 'checkbox' ? checked : value;

    // Handle Listener Protocol Port logic
    if (name === 'lb_protocol') {
      if (value === 'HTTP') {
        stackForm.setFieldValue(name, value);
        stackForm.setFieldValue('lb_protocol_port', 80);
        stackForm.setFieldValue('default_tls_container_ref', '');
      } else if (value === 'HTTPS') {
        stackForm.setFieldValue(name, value);
        stackForm.setFieldValue('lb_protocol_port', 443);
        stackForm.setFieldValue('default_tls_container_ref', '');
      } else if (value === 'TERMINATED_HTTPS') {
        stackForm.setFieldValue(name, value);
        stackForm.setFieldValue('lb_protocol_port', 443);
        // Don't reset if it's already set (e.g. during edit)
        if (!stackForm.values.default_tls_container_ref) {
          stackForm.setFieldValue('default_tls_container_ref', '');
        }
      } else {
        stackForm.setFieldValue(name, value);
        stackForm.setFieldValue('lb_protocol_port', 0);
        stackForm.setFieldValue('default_tls_container_ref', '');
      }
      return;
    }

    // Handle network change - filter subnets
    if (name === 'network_id') {
      const networkSubnets = subnets.filter(
        (subnet) => subnet.network_id === value,
      );
      setFilteredSubnets(networkSubnets);
      stackForm.setFieldValue(name, value);
      stackForm.setFieldValue('subnet_id', '');
      return;
    }

    // Handle ASG Desired Capacity - default to min if not set, validate range
    if (
      name === 'asg_min_size' ||
      name === 'asg_max_size' ||
      name === 'asg_desired_capacity'
    ) {
      // Allow empty string for clearing the field
      if (value === '' || value === null || value === undefined) {
        stackForm.setFieldValue(name, '');
        // If clearing min_size, also clear desired if it was equal to min
        if (name === 'asg_min_size') {
          const oldMin = Number(stackForm.values.asg_min_size) || 0;
          const oldDesired = Number(stackForm.values.asg_desired_capacity) || 0;
          if (oldDesired === oldMin) {
            stackForm.setFieldValue('asg_desired_capacity', '');
          }
        }
        return;
      }

      const numValue = Number(value);
      // Prevent negative values and invalid numbers
      if (isNaN(numValue) || numValue < 0) {
        return;
      }

      // If min_size changes and desired is 0 or equal to old min, update desired to new min
      if (name === 'asg_min_size') {
        const oldMin = Number(stackForm.values.asg_min_size) || 0;
        const desired = Number(stackForm.values.asg_desired_capacity) || 0;
        stackForm.setFieldValue(name, numValue);
        if (desired === 0 || desired === oldMin) {
          stackForm.setFieldValue('asg_desired_capacity', numValue);
        }
        return;
      }

      // Validate desired is between min and max (only reject if user is changing desired)
      if (name === 'asg_desired_capacity') {
        const min = Number(stackForm.values.asg_min_size) || 0;
        const max = Number(stackForm.values.asg_max_size) || 0;
        if (numValue > 0 && (numValue < min || (max > 0 && numValue > max))) {
          // Don't update if out of range
          return;
        }
      }

      stackForm.setFieldValue(name, numValue);
      return;
    }

    // Handle percentage fields (CPU and RAM thresholds) - must be between 1 and 100
    const percentageFields = [
      'cpu_scale_up_threshold_percent',
      'cpu_scale_down_threshold_percent',
      'ram_scale_up_threshold_percent',
      'ram_scale_down_threshold_percent',
    ];

    if (type === 'number' && percentageFields.includes(name)) {
      // If value is empty string, allow it (for clearing the field)
      if (value === '' || value === null || value === undefined) {
        stackForm.setFieldValue(name, '');
        return;
      }

      const numValue = Number(value);
      // Reject negative numbers or values greater than 100
      if (isNaN(numValue) || numValue < 0 || numValue > 100) {
        return;
      }

      // Update the value
      stackForm.setFieldValue(name, numValue);
      return;
    }

    // Prevent negative numbers for other numeric fields
    if (type === 'number') {
      // If value is empty string, allow it (for clearing the field)
      if (value === '' || value === null || value === undefined) {
        stackForm.setFieldValue(name, '');
        return;
      }

      const numValue = Number(value);
      // Reject negative numbers - return early without updating
      if (isNaN(numValue) || numValue < 0) {
        return;
      }
      stackForm.setFieldValue(name, numValue);
      return;
    }

    stackForm.setFieldValue(name, nextValue);
    stackForm.setFieldTouched(name, false);
  };

  const handleEditStack = async (stackId, stackName) => {
    setIsEditMode(true);
    setEditingStackId(stackId);
    setEditingStackName(stackName);
    setIsOpen(true);
    setIsFetchingDetails(true);

    try {
      // THE ULTIMATE FETCH: Get every possible resource list to ensure 100% matching capability
      const [
        stackRes,
        snapRes,
        imgRes,
        netRes,
        subRes,
        flavRes,
        keyRes,
        sgRes,
      ] = await Promise.all([
        apiEndPoints.getStack(projectId, stackId, stackName),
        apiEndPoints.listServerSnapshots(projectId),
        apiEndPoints.listImages(), // Essential for standard OS images
        apiEndPoints.getNetworks(projectId),
        apiEndPoints.getSubNetworks(projectId),
        apiEndPoints.listFlavours(),
        apiEndPoints.listKeypairs(projectId),
        apiEndPoints.listSecurityGroups(projectId),
      ]);

      if (!stackRes?.data?.stack) {
        throw new Error('Stack data not found in response');
      }

      const stackData = stackRes.data.stack;
      const params = stackData.parameters || {};

      // Robust helper to ensure we are always working with an array
      // This prevents "is not iterable" errors if the API structure changes
      const asArray = (val) => (Array.isArray(val) ? val : []);

      const currentSnapshots = asArray(
        snapRes?.data?.snapshots || snapRes?.data,
      );
      const currentImages = asArray(imgRes?.data?.images || imgRes?.data);
      const currentNetworks = asArray(netRes?.data?.networks || netRes?.data);
      const currentSubnets = asArray(subRes?.data?.subnets || subRes?.data);
      const currentFlavors = asArray(
        flavRes?.data?.results || flavRes?.data?.flavours || flavRes?.data,
      );
      const currentKeypairs = asArray(keyRes?.data?.keypairs || keyRes?.data);
      const currentSecurityGroups = asArray(
        sgRes?.data?.security_groups || sgRes?.data,
      );

      // Combine snapshots and standard images - this is critical for Image Name selection
      const allPossibleImages = [...currentSnapshots, ...currentImages];

      // Update local state variables so the UI dropdowns have all possible options
      setSnapshots(allPossibleImages);
      setNetworks(currentNetworks);
      setSubNets(currentSubnets);
      setFlavors(currentFlavors);
      setKeyPairs(currentKeypairs);
      setSecurityGroups(currentSecurityGroups);

      // Helper function to parse numeric values (handles "10.0 %" strings)
      const parseNumericValue = (value, defaultValue = 0) => {
        if (value === null || value === undefined || value === '') {
          return defaultValue;
        }
        if (typeof value === 'number') {
          return value;
        }
        if (typeof value === 'string') {
          const cleaned = value.replace(/%/g, '').trim();
          const num = parseFloat(cleaned);
          return isNaN(num) ? defaultValue : num;
        }
        return defaultValue;
      };

      // THE ULTIMATE RESOLVER: Matches by ID or Name (case-insensitive)
      const resolveIdUltimate = (list, val) => {
        if (!val) return '';
        const searchVal = String(val).toLowerCase().trim();
        const match = list?.find(
          (item) =>
            String(item.id || '')
              .toLowerCase()
              .trim() === searchVal ||
            String(item.name || '')
              .toLowerCase()
              .trim() === searchVal,
        );
        return match ? match.id : val;
      };

      // Parse and resolve Security Groups (which might come as names)
      const rawSGs = params.security_groups
        ? params.security_groups.split(',').filter(Boolean)
        : [];
      const resolvedSGs = rawSGs.map((sg) =>
        resolveIdUltimate(currentSecurityGroups, sg),
      );

      // Map everything using the Ultimate Resolver
      const formData = {
        image_id: resolveIdUltimate(
          allPossibleImages,
          params.image_id || params.image || params.image_name,
        ),
        flavor: resolveIdUltimate(
          currentFlavors,
          params.flavor || params.flavor_id,
        ),
        network_id: resolveIdUltimate(
          currentNetworks,
          params.network_id || params.network || params.network_name,
        ),
        subnet_id: resolveIdUltimate(
          currentSubnets,
          params.subnet_id || params.subnet || params.subnet_name,
        ),
        key_name: params.key_name || '',
        vn_name: params.vn_name || '',
        security_groups: resolvedSGs,
        app_port: parseNumericValue(params.app_port, 80),
        asg_desired_capacity: parseNumericValue(params.asg_desired_capacity, 1),
        asg_min_size: parseNumericValue(params.asg_min_size, 1),
        asg_max_size: parseNumericValue(params.asg_max_size, 2),
        asg_cooldown: parseNumericValue(params.asg_cooldown, 60),
        scale_up_adjustment: parseNumericValue(params.scale_up_adjustment, 1),
        scale_down_adjustment: Math.abs(
          parseNumericValue(params.scale_down_adjustment, 1),
        ),
        scaling_cooldown: parseNumericValue(params.scaling_cooldown, 60),
        cpu_scale_up_threshold_percent: parseNumericValue(
          params.cpu_scale_up_threshold_percent ||
            params.cpu_scale_up_threshold,
          1,
        ),
        cpu_scale_down_threshold_percent: parseNumericValue(
          params.cpu_scale_down_threshold_percent ||
            params.cpu_scale_down_threshold,
          1,
        ),
        cpu_granularity: parseNumericValue(params.cpu_granularity, 300),
        cpu_evaluation_periods: parseNumericValue(
          params.cpu_evaluation_periods,
          1,
        ),
        ram_scale_up_threshold_percent: parseNumericValue(
          params.ram_scale_up_threshold_percent ||
            params.ram_scale_up_threshold_mb,
          1,
        ),
        ram_scale_down_threshold_percent: parseNumericValue(
          params.ram_scale_down_threshold_percent ||
            params.ram_scale_down_threshold_mb,
          1,
        ),
        ram_granularity: parseNumericValue(params.ram_granularity, 300),
        ram_evaluation_periods: parseNumericValue(
          params.ram_evaluation_periods,
          1,
        ),
        lb_protocol: params.lb_protocol || '',
        lb_protocol_port: parseNumericValue(params.lb_protocol_port, 1),
        lb_algorithm: params.lb_algorithm || '',
        health_check_type: params.health_check_type || '',
        health_check_delay: parseNumericValue(params.health_check_delay, 60),
        health_check_timeout: parseNumericValue(params.health_check_timeout, 0),
        health_check_max_retries: parseNumericValue(
          params.health_check_max_retries,
          1,
        ),
        health_check_url_path: params.health_check_url_path || '/',
        health_check_expected_codes:
          params.health_check_expected_codes || '200',
        default_tls_container_ref: params.default_tls_container_ref || '',
        pool_protocol: params.pool_protocol || 'HTTP',
      };

      // Synchronously prepare filtered subnets to prevent race conditions
      if (formData.network_id) {
        setFilteredSubnets(
          currentSubnets.filter((s) => s.network_id === formData.network_id),
        );
      }

      stackForm.setValues(formData);
    } catch (error) {
      const msg = extractAxiosErrorMessage(error);
      toast.error(msg || 'Error loading stack data');
      setIsOpen(false);
      setIsEditMode(false);
      setEditingStackId(null);
      setEditingStackName(null);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleCreateStack = async (values, setSubmitting) => {
    setIsCreating(true);

    // Validate and adjust ASG capacity
    const min = Number(values.asg_min_size) || 0;
    const max = Number(values.asg_max_size) || 0;
    let desired = Number(values.asg_desired_capacity) || min;

    // Ensure desired defaults to min if not set
    if (desired === 0 && min > 0) {
      desired = min;
    }

    // Ensure desired is within bounds
    if (desired < min) desired = min;
    if (desired > max && max > 0) desired = max;

    // Get VCPUs from selected flavor (only for create, not update)
    let vcpus = 1;
    if (!isEditMode) {
      const selectedFlavor = flavors.find((f) => f.id === values.flavor);
      vcpus = selectedFlavor?.vcpus || selectedFlavor?.vCPUs || 1;
    }

    // Convert RAM thresholds from percentage to MB format for API (if needed)
    // Note: Check with backend if they expect percentage or MB
    const payload = {
      ...values,
      asg_desired_capacity: desired,
      // Keep both for now - backend will determine which to use
      ram_scale_up_threshold_mb: values.ram_scale_up_threshold_percent,
      ram_scale_down_threshold_mb: values.ram_scale_down_threshold_percent,
      // pool_protocol is always HTTP
      pool_protocol: 'HTTP',
    };

    // For create mode, add flavor, security_groups, image_id, and vcpus
    if (!isEditMode) {
      const secGroupsArr = values.security_groups;
      const secGroups = secGroupsArr.join(',');
      payload.vcpus = vcpus;
      payload.security_groups = secGroups;
    } else {
      // For update, remove fields that cannot be updated
      // These fields cannot be edited: image, flavor, network_id, subnet_id, key_name, vn_name, security_groups
      delete payload.image;
      delete payload.image_id;
      delete payload.flavor;
      delete payload.network_id;
      delete payload.subnet_id;
      delete payload.key_name;
      delete payload.vn_name;
      delete payload.security_groups;
      delete payload.vcpus; // Also remove vcpus as it's derived from flavor
    }

    try {
      // Remove empty strings from select fields - API doesn't accept them
      const finalPayload = { ...payload };

      // Remove empty strings for select fields that require valid choices
      if (finalPayload.lb_protocol === '') {
        delete finalPayload.lb_protocol;
      }
      if (finalPayload.lb_algorithm === '') {
        delete finalPayload.lb_algorithm;
      }
      if (finalPayload.health_check_type === '') {
        delete finalPayload.health_check_type;
      }
      // Explicitly handle default_tls_container_ref - if protocol is not TERMINATED_HTTPS, remove it
      if (finalPayload.lb_protocol !== 'TERMINATED_HTTPS') {
        delete finalPayload.default_tls_container_ref;
      }

      // Remove any other empty string values
      Object.keys(finalPayload).forEach((key) => {
        if (finalPayload[key] === '') {
          delete finalPayload[key];
        }
      });

      if (isEditMode) {
        await apiEndPoints.updateStack(
          projectId,
          editingStackId,
          editingStackName,
          finalPayload,
        );
        toast.success('Stack updated successfully');
      } else {
        await apiEndPoints.createStack(projectId, finalPayload);
        toast.success('Stack created successfully');
      }

      stackForm.resetForm();
      setFilteredSubnets([]);
      setIsEditMode(false);
      setEditingStackId(null);
      setEditingStackName(null);
      setIsOpen(false);
      fetchStacks();
    } catch (error) {
      const msg = extractAxiosErrorMessage(error);
      toast.error(msg);
    } finally {
      setIsCreating(false);
      if (setSubmitting) setSubmitting(false);
    }
  };

  const fetchServerSnapshots = useCallback(async () => {
    try {
      const { data } = await apiEndPoints.listServerSnapshots(projectId);
      setSnapshots(data.snapshots);
    } catch (error) {
      console.error(error);
    }
  }, [projectId]);
  const fetchFlavors = useCallback(async () => {
    try {
      const { data } = await apiEndPoints.listFlavours();
      setFlavors(data.results);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchNetworks = useCallback(async () => {
    try {
      const { data } = await apiEndPoints.getNetworks(projectId);
      setNetworks(data.networks);
    } catch (error) {
      console.error(error);
    }
  }, [projectId]);

  const fetchSubNets = useCallback(async () => {
    try {
      const { data } = await apiEndPoints.getSubNetworks(projectId);
      setSubNets(data.subnets);
    } catch (error) {
      console.error(error);
    }
  }, [projectId]);
  const fetchKeyPairs = useCallback(async () => {
    try {
      const { data } = await apiEndPoints.listKeypairs(projectId);
      setKeyPairs(data.keypairs);
    } catch (error) {
      console.error(error);
    }
  }, [projectId]);
  const fetchSecurityGroups = useCallback(async () => {
    try {
      const { data } = await apiEndPoints.listSecurityGroups(projectId);
      setSecurityGroups(data.security_groups);
    } catch (error) {
      console.error(error);
    }
  }, [projectId]);

  const fetchCertificates = useCallback(async () => {
    try {
      if (projectId) {
        // Using getSecrets thunk from userReducer.js
        // On success: returns response.data?.secrets || []
        // On error: returns error.response?.data (which may not be an array)
        const result = await dispatch(getSecrets(projectId));
        // Ensure we always set an array - the thunk returns array on success, but error.response?.data on error
        const certificatesData = result?.payload;
        setCertificates(
          Array.isArray(certificatesData) ? certificatesData : [],
        );
      }
    } catch (error) {
      console.error(error);
      setCertificates([]);
    }
  }, [projectId, dispatch]);

  const fetchStacks = useCallback(async () => {
    try {
      const { data } = await apiEndPoints.listStacks(projectId);
      setStacks(data.stacks);
    } catch (error) {
      const msg = extractAxiosErrorMessage(error);
      toast.error(msg);
    }
  }, [projectId]);

  const handleDelete = async () => {
    if (!target) return;
    const parts = target.split(':');
    if (parts.length < 2) {
      toast.error('Invalid stack information');
      setTarget(null);
      return;
    }
    const [id, name] = parts;
    setIsDeleting(true);
    try {
      const res = await apiEndPoints.deleteStack(projectId, id, name);
      setTarget(null);
      toast.success(res.data.message);
      window.location.reload();
    } catch (error) {
      const msg = extractAxiosErrorMessage(error);
      toast.error(msg || 'Error deleting stack');
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchStackDetails = useCallback(async () => {
    if (!detailsTarget) return;
    setIsFetchingDetails(true);
    const [id, name] = detailsTarget.split(':');
    try {
      const { data } = await apiEndPoints.getStack(projectId, id, name);
      setStackPayload(data.stack);
    } catch (error) {
      const msg = extractAxiosErrorMessage(error);
      toast.error(msg || 'Error fetching stack details');
    } finally {
      setIsFetchingDetails(false);
    }
  }, [detailsTarget, projectId]);

  useEffect(() => {
    fetchFlavors();
    fetchServerSnapshots();
    fetchNetworks();
    fetchSubNets();
    fetchKeyPairs();
    fetchSecurityGroups();
    fetchCertificates();
    fetchStacks();
  }, [
    fetchFlavors,
    fetchNetworks,
    fetchServerSnapshots,
    projectId,
    fetchSubNets,
    fetchKeyPairs,
    fetchSecurityGroups,
    fetchCertificates,
    fetchStacks,
  ]);

  // Reset form when drawer opens
  // Reset form when drawer opens (only for create mode, not edit mode)
  useEffect(() => {
    if (isOpen && !isEditMode) {
      stackForm.resetForm();
      setFilteredSubnets([]);
      setSelectedFlavorDetails(null);
      setSecurityGroupsDropdownOpen(false);
      setSecurityGroupsSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode]);

  // Filter secrets to only show certificate type secrets
  // Use secret_ref as value and name as label for the dropdown
  const certificateOptions = [
    { value: '', label: 'Select certificate' },
    ...(Array.isArray(certificates) ? certificates : [])
      .filter((secret) => secret?.secret_type === 'certificate')
      .map((secret) => ({
        value: secret?.secret_ref || '',
        label: secret?.name || secret?.secret_id || 'Unnamed Certificate',
      })),
  ];

  // Ensure external network is selected when networks are loaded or drawer opens
  //TODOS: group api calls based on params or no params

  function StatusBadge({ status }) {
    const s = String(status || '').toUpperCase();
    let cls =
      'inline-flex items-center rounded px-2 py-0.5 text-xs ring-1 bg-gray-100 text-gray-800 ring-gray-200';
    if (s.includes('FAILED') || s.includes('ERROR')) {
      cls =
        'inline-flex items-center rounded px-2 py-0.5 text-xs ring-1 bg-red-100 text-red-800 ring-red-200';
    } else if (s.includes('COMPLETE') || s.includes('SUCCESS')) {
      cls =
        'inline-flex items-center rounded px-2 py-0.5 text-xs ring-1 bg-green-100 text-green-800 ring-green-200';
    } else if (s.includes('IN_PROGRESS')) {
      cls =
        'inline-flex items-center rounded px-2 py-0.5 text-xs ring-1 bg-amber-100 text-amber-800 ring-amber-200';
    }
    return <span className={cls}>{status}</span>;
  }

  // Close on outside click or Escape
  useEffect(() => {
    if (!openMenuId) return;
    const onDocClick = (e) => {
      const menuRef = menuRefs.current[openMenuId];
      if (!menuRef) return;
      if (!menuRef.contains(e.target)) setOpenMenuId(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenMenuId(null);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenuId]);

  // Close security groups dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        securityGroupsDropdownRef.current &&
        !securityGroupsDropdownRef.current.contains(event.target)
      ) {
        setSecurityGroupsDropdownOpen(false);
        setSecurityGroupsSearch('');
      }
    };
    if (securityGroupsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [securityGroupsDropdownOpen]);

  // DOM manipulation to ensure Subnet ID and VM Name fields are always accessible
  useEffect(() => {
    const ensureFieldsAccessible = () => {
      // Fix Subnet ID field
      if (subnetIdRef.current) {
        const selectElement = subnetIdRef.current.querySelector('select');
        if (selectElement) {
          selectElement.style.pointerEvents = 'auto';
          selectElement.style.zIndex = '10';
          selectElement.style.position = 'relative';
          // Remove any overlaying elements
          const rect = selectElement.getBoundingClientRect();
          const elementsAtPoint = document.elementsFromPoint(
            rect.left + rect.width / 2,
            rect.top + rect.height / 2,
          );
          elementsAtPoint.forEach((el) => {
            if (
              el !== selectElement &&
              el !== subnetIdRef.current &&
              !subnetIdRef.current.contains(el)
            ) {
              const computedStyle = window.getComputedStyle(el);
              if (computedStyle.pointerEvents !== 'none' && el.style) {
                // Check if this element is overlaying our field
                const elRect = el.getBoundingClientRect();
                if (
                  elRect.left < rect.right &&
                  elRect.right > rect.left &&
                  elRect.top < rect.bottom &&
                  elRect.bottom > rect.top
                ) {
                  // Temporarily disable pointer events on overlaying element if it's not interactive
                  if (
                    !el.querySelector('input') &&
                    !el.querySelector('select') &&
                    !el.querySelector('button') &&
                    !el.querySelector('[role="button"]') &&
                    el !== securityGroupsDropdownRef.current
                  ) {
                    el.style.pointerEvents = 'none';
                  }
                }
              }
            }
          });
        }
      }

      // Fix VM Name field
      if (vnNameRef.current) {
        const inputElement = vnNameRef.current.querySelector('input');
        if (inputElement) {
          inputElement.style.pointerEvents = 'auto';
          inputElement.style.zIndex = '10';
          inputElement.style.position = 'relative';
          // Remove any overlaying elements
          const rect = inputElement.getBoundingClientRect();
          const elementsAtPoint = document.elementsFromPoint(
            rect.left + rect.width / 2,
            rect.top + rect.height / 2,
          );
          elementsAtPoint.forEach((el) => {
            if (
              el !== inputElement &&
              el !== vnNameRef.current &&
              !vnNameRef.current.contains(el)
            ) {
              const computedStyle = window.getComputedStyle(el);
              if (computedStyle.pointerEvents !== 'none' && el.style) {
                // Check if this element is overlaying our field
                const elRect = el.getBoundingClientRect();
                if (
                  elRect.left < rect.right &&
                  elRect.right > rect.left &&
                  elRect.top < rect.bottom &&
                  elRect.bottom > rect.top
                ) {
                  // Temporarily disable pointer events on overlaying element if it's not interactive
                  if (
                    !el.querySelector('input') &&
                    !el.querySelector('select') &&
                    !el.querySelector('button') &&
                    !el.querySelector('[role="button"]') &&
                    el !== securityGroupsDropdownRef.current
                  ) {
                    el.style.pointerEvents = 'none';
                  }
                }
              }
            }
          });
        }
      }
    };

    // Run on mount and when drawer opens
    if (isOpen) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(ensureFieldsAccessible, 100);
      // Also run on window resize
      window.addEventListener('resize', ensureFieldsAccessible);
      // Run periodically to catch any dynamic changes
      const intervalId = setInterval(ensureFieldsAccessible, 500);

      return () => {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
        window.removeEventListener('resize', ensureFieldsAccessible);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (detailsTarget) {
      fetchStackDetails();
    }
    return;
  }, [detailsTarget, fetchStackDetails]);

  // Set desired capacity to min when min changes and desired is 0
  useEffect(() => {
    if (
      stackForm.values.asg_min_size > 0 &&
      stackForm.values.asg_desired_capacity === 0
    ) {
      stackForm.setFieldValue(
        'asg_desired_capacity',
        stackForm.values.asg_min_size,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackForm.values.asg_min_size, stackForm.values.asg_desired_capacity]);

  // Filter subnets when network changes
  useEffect(() => {
    if (stackForm.values.network_id) {
      const networkSubnets = subnets.filter(
        (subnet) => subnet.network_id === stackForm.values.network_id,
      );
      setFilteredSubnets(networkSubnets);
    } else {
      setFilteredSubnets([]);
    }
  }, [stackForm.values.network_id, subnets]);

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="border-0 text-primaryBlue cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft size={20} />
          </Button>
          <p className="crimson-font text-lg font-medium text-black dark:text-white sm:text-xl">
            Auto Scaling
          </p>
        </div>

        <Button
          className="w-full sm:w-auto py-2 px-3 cursor-pointer bg-[#1da1f1]"
          size="sm"
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <PlusIcon className="mr-2" />
          New Stack
        </Button>
      </div>
      <div className="overflow-x-visible overflow-y-visible rounded-lg border bg-white mt-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500 min-w-[200px]">
                Name
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500 min-w-[120px]">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500 min-w-[250px]">
                Reason
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500 min-w-[180px]">
                Created
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium uppercase tracking-wider text-gray-500 min-w-[80px]">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 bg-white">
            {stacks.length === 0 ? (
              <tr>
                <td
                  className="px-6 py-8 text-sm text-gray-500 text-center"
                  colSpan={5}
                >
                  No stacks found.
                </td>
              </tr>
            ) : (
              stacks.map((o) => {
                const id = o.id;
                const name = o.stack_name || id;
                const status = o.stack_status || '—';
                const reason = o.stack_status_reason || '—';
                const created = o.creation_time
                  ? new Date(o.creation_time).toLocaleString()
                  : '—';
                //const stackOwner = o.stack_owner || "-";

                return (
                  <tr key={id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        className="text-sm text-primaryBlue truncate max-w-[200px] underline decoration-transparent hover:decoration-inherit"
                        onClick={() => {
                          setDetailsTarget(`${id}:${name}`);
                          setOpenDetails(true);
                        }}
                        title={name}
                        type="button"
                      >
                        {name}
                      </button>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={status} />
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-start gap-2 max-w-[250px]">
                        <span
                          className="text-sm text-gray-700 line-clamp-2 break-words flex-1"
                          title={reason}
                        >
                          {reason && reason.includes(':')
                            ? reason.split(':')[3]
                            : reason}
                        </span>
                        {reason && reason.toLowerCase().includes('failed') && (
                          <Tooltip
                            content={reason}
                            placement="bottom"
                            className="w-[300px] h-[100px] bg-red-600 overflow-y-auto"
                          >
                            <BsInfoCircle
                              size={14}
                              className="cursor-pointer text-red-600 flex-shrink-0 mt-0.5"
                            />
                          </Tooltip>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{created}</span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {/* Three Menu dots*/}
                      <div className="relative inline-block">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          aria-haspopup="menu"
                          aria-expanded={openMenuId === id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === id ? null : id);
                          }}
                        >
                          <HiOutlineDotsVertical className="h-5 w-5" />
                          <span className="sr-only">Open menu</span>
                        </button>
                        {/* Dropdown Menu*/}
                        {openMenuId === id && (
                          <div
                            ref={(el) => {
                              if (el) {
                                menuRefs.current[id] = el;
                              } else {
                                delete menuRefs.current[id];
                              }
                            }}
                            role="menu"
                            aria-label="stack actions"
                            className="absolute right-0 mt-2 z-50 w-40 origin-top-right rounded-md border border-gray-200 bg-white shadow-lg focus:outline-none"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                              role="menuitem"
                              onClick={() => {
                                setOpenMenuId(null);
                                setDetailsTarget(`${id}:${name}`);
                                setOpenDetails(true);
                              }}
                            >
                              Details
                            </button>

                            <button
                              className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                              role="menuitem"
                              onClick={() => {
                                setOpenMenuId(null);
                                handleEditStack(id, name);
                              }}
                            >
                              Edit
                            </button>

                            <button
                              className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                              role="menuitem"
                              onClick={() => {
                                setOpenMenuId(null);
                                setTarget(`${id}:${name}`);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <StackDetailsModal
          stackPayload={stackPayload}
          open={openDetails}
          setOpen={setOpenDetails}
          isFetching={isfetchingDetails}
        />
        {/* Delete confirmation */}
        <ConfirmDeleteModal
          open={!!target}
          title="Delete Auto Scaling Group"
          name={target ? target.split(':')[1] : ''}
          onClose={() => setTarget(null)}
          onConfirm={handleDelete}
          description="To confirm deletion, type the auto scaling group name exactly"
          isDeleting={isDeleting}
        />
      </div>
      <div className="mt-4">
        <Drawer
          title={isEditMode ? 'Edit Stack' : 'New Stack'}
          open={isOpen}
          onClose={() => {
            setIsOpen(false);
            setIsEditMode(false);
            setEditingStackId(null);
            setEditingStackName(null);
            stackForm.resetForm();
            setFilteredSubnets([]);
            setSelectedFlavorDetails(null);
            setSecurityGroupsDropdownOpen(false);
            setSecurityGroupsSearch('');
          }}
        >
          {isOpen && (
            <AutoscaleBillingNotice open={noticeOpen} setOpen={setNoticeOpen} />
          )}
          {isfetchingDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primaryBlue" />
                <p className="text-sm text-gray-600">
                  Loading stack details...
                </p>
              </div>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={stackForm.handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Image Name
                    <Tooltip
                      content="Note: The images displayed here should be a snapshot of the deployment you want to auto scale."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <select
                    name="image_id"
                    value={stackForm.values.image_id}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] focus:outline-0 dark:bg-slate-800 dark:text-white"
                    disabled={isCreating || isEditMode}
                  >
                    <option value="">Select Image</option>
                    {snapshots.map((snap) => (
                      <option key={snap.id} value={snap.id}>
                        {snap.name}
                      </option>
                    ))}
                  </select>
                  {stackForm.touched.image_id && stackForm.errors.image_id && (
                    <p className="text-xs text-red-500 mt-1">
                      {stackForm.errors.image_id}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block mb-1 text-sm">Flavor</label>
                  <div className="flex gap-2">
                    <select
                      name="flavor"
                      value={stackForm.values.flavor}
                      onChange={(e) => {
                        handleChange(e);
                        const selectedFlavor = flavors.find(
                          (f) => f.id === e.target.value,
                        );
                        if (selectedFlavor) {
                          setSelectedFlavorDetails(selectedFlavor);
                          setShowFlavorModal(true);
                        }
                      }}
                      onBlur={stackForm.handleBlur}
                      className="flex-1 bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] focus:outline-0 dark:bg-slate-800 dark:text-white"
                      disabled={isCreating || isEditMode}
                    >
                      <option value="">Select flavor</option>
                      {flavors.map((flav) => (
                        <option key={flav.id} value={flav.id}>
                          {flav.name}
                        </option>
                      ))}
                    </select>
                    {stackForm.values.flavor && (
                      <button
                        type="button"
                        onClick={() => {
                          const selectedFlavor = flavors.find(
                            (f) => f.id === stackForm.values.flavor,
                          );
                          if (selectedFlavor) {
                            setSelectedFlavorDetails(selectedFlavor);
                            setShowFlavorModal(true);
                          }
                        }}
                        className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                        disabled={isCreating}
                      >
                        View Details
                      </button>
                    )}
                  </div>
                  {stackForm.touched.flavor && stackForm.errors.flavor && (
                    <p className="text-xs text-red-500 mt-1">
                      {stackForm.errors.flavor}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block mb-1 text-sm">Key</label>
                  <Select
                    name="key_name"
                    value={stackForm.values.key_name}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    disabled={isCreating || isEditMode}
                  >
                    <option value="">Select key</option>
                    {keypairs.map((key) => (
                      <option key={key.keypair.name} value={key.keypair.name}>
                        {key.keypair.name}
                      </option>
                    ))}
                  </Select>
                  {stackForm.touched.key_name && stackForm.errors.key_name && (
                    <p className="text-xs text-red-500 mt-1">
                      {stackForm.errors.key_name}
                    </p>
                  )}
                </div>
                <div className="">
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Network ID
                    <Tooltip
                      content={
                        networks.length === 0
                          ? 'No networks available. External network will be used by default.'
                          : 'If no private network is selected, the public network will be used by default.'
                      }
                      className="!bg-primaryBlue !text-white"
                      placement="left"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Select
                    name="network_id"
                    value={stackForm.values.network_id}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    disabled={isCreating || isEditMode}
                  >
                    <option value="">Select network</option>
                    {networks.map((ntwk) => (
                      <option key={ntwk.id} value={ntwk.id}>
                        {ntwk.name || ntwk.id}
                      </option>
                    ))}
                  </Select>
                  {stackForm.touched.network_id &&
                    stackForm.errors.network_id && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.network_id}
                      </p>
                    )}
                </div>
                <div
                  className="relative"
                  ref={subnetIdRef}
                  style={{
                    overflow: 'visible',
                    pointerEvents: 'auto',
                    zIndex: 10,
                  }}
                >
                  <label className="mb-1 text-sm flex items-center gap-2">
                    SubNet ID
                    {stackForm.values.network_id &&
                      filteredSubnets.length === 0 && (
                        <Tooltip
                          content="No subnets available for selected network. External subnet will be used by default."
                          className="!bg-primaryBlue !text-white"
                          placement="right"
                        >
                          <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                        </Tooltip>
                      )}
                  </label>
                  <div
                    style={{
                      position: 'relative',
                      overflow: 'visible',
                      pointerEvents: 'auto',
                      zIndex: 10,
                    }}
                  >
                    <Select
                      name="subnet_id"
                      value={stackForm.values.subnet_id}
                      onChange={(e) => {
                        handleChange(e);
                        setSecurityGroupsDropdownOpen(false);
                      }}
                      onBlur={stackForm.handleBlur}
                      disabled={isCreating || isEditMode}
                    >
                      <option value="">Select subnet</option>
                      {(stackForm.values.network_id
                        ? filteredSubnets
                        : subnets
                      ).map((sntwk) => (
                        <option key={sntwk.id} value={sntwk.id}>
                          {sntwk.name || sntwk.id}
                        </option>
                      ))}
                    </Select>
                  </div>
                  {stackForm.touched.subnet_id &&
                    stackForm.errors.subnet_id && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.subnet_id}
                      </p>
                    )}
                </div>
                <div
                  ref={vnNameRef}
                  style={{
                    pointerEvents: 'auto',
                    zIndex: 10,
                    position: '',
                  }}
                >
                  <label className="mb-1 text-sm flex items-center gap-2">
                    VM Name
                    <Tooltip
                      content="The VM Name must be unique across the project."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <div
                    style={{
                      pointerEvents: 'auto',
                      zIndex: 10,
                      position: 'relative',
                    }}
                  >
                    <Input
                      type="text"
                      name="vn_name"
                      value={stackForm.values.vn_name}
                      onChange={handleChange}
                      onBlur={stackForm.handleBlur}
                      disabled={isCreating || isEditMode}
                    />
                  </div>
                  {stackForm.touched.vn_name && stackForm.errors.vn_name && (
                    <p className="text-xs text-red-500 mt-1">
                      {stackForm.errors.vn_name}
                    </p>
                  )}
                </div>
                <div className="relative" ref={securityGroupsDropdownRef}>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Security Groups
                    <Tooltip
                      content="Select one or more security groups to apply to the instances."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>

                  {/* Selected Security Groups Display */}
                  <div
                    className={`bg-white dark:bg-slate-800 w-full min-h-[48px] p-2 text-sm rounded-md border border-[#c4c4c460] flex flex-wrap gap-2 items-center ${
                      isCreating || isEditMode
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}
                    onClick={() =>
                      !isCreating &&
                      !isEditMode &&
                      setSecurityGroupsDropdownOpen(!securityGroupsDropdownOpen)
                    }
                  >
                    {stackForm.values.security_groups?.length > 0 ? (
                      <>
                        {stackForm.values.security_groups.map((sg) => (
                          <span
                            key={sg}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded text-xs font-medium"
                          >
                            {sg}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isEditMode) {
                                  const current =
                                    stackForm.values.security_groups || [];
                                  stackForm.setFieldValue(
                                    'security_groups',
                                    current.filter((g) => g !== sg),
                                  );
                                }
                              }}
                              className="hover:text-red-600 dark:hover:text-red-400 font-bold text-sm leading-none"
                              disabled={isEditMode}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">
                        Select security groups...
                      </span>
                    )}
                    <div className="ml-auto">
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${securityGroupsDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Dropdown Menu */}
                  {securityGroupsDropdownOpen && (
                    <div
                      className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-[#c4c4c460] rounded-md shadow-lg max-h-60 overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {/* Search Input */}
                      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                        <input
                          type="text"
                          placeholder="Search security groups..."
                          value={securityGroupsSearch}
                          onChange={(e) =>
                            setSecurityGroupsSearch(e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-slate-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          disabled={isCreating}
                        />
                      </div>

                      {/* Options List */}
                      <div className="max-h-48 overflow-y-auto">
                        {securityGroups.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                            No security groups available
                          </div>
                        ) : (
                          (() => {
                            const filtered = securityGroups.filter((sg) =>
                              sg.name
                                .toLowerCase()
                                .includes(securityGroupsSearch.toLowerCase()),
                            );
                            return filtered.length === 0 ? (
                              <div className="p-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                                No security groups found
                              </div>
                            ) : (
                              filtered.map((secGroup) => {
                                const isSelected =
                                  stackForm.values.security_groups?.includes(
                                    secGroup.name,
                                  );
                                return (
                                  <div
                                    key={secGroup.id}
                                    className={`flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-slate-700 ${
                                      isEditMode
                                        ? 'cursor-not-allowed opacity-50'
                                        : 'cursor-pointer'
                                    } ${
                                      isSelected
                                        ? 'bg-blue-50 dark:bg-blue-900/20'
                                        : ''
                                    }`}
                                    onClick={(e) => {
                                      if (isCreating || isEditMode) return;
                                      e.stopPropagation();
                                      const current = Array.isArray(
                                        stackForm.values.security_groups,
                                      )
                                        ? stackForm.values.security_groups
                                        : [];
                                      const has = current.includes(
                                        secGroup.name,
                                      );
                                      stackForm.setFieldValue(
                                        'security_groups',
                                        has
                                          ? current.filter(
                                              (g) => g !== secGroup.name,
                                            )
                                          : [...current, secGroup.name],
                                      );
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {}}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      disabled={isCreating}
                                    />
                                    <span className="text-sm text-gray-900 dark:text-white flex-1">
                                      {secGroup.name}
                                    </span>
                                    {isSelected && (
                                      <svg
                                        className="w-4 h-4 text-blue-600 dark:text-blue-400"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                );
                              })
                            );
                          })()
                        )}
                      </div>
                    </div>
                  )}

                  {/* Error message */}
                  {stackForm.touched.security_groups &&
                    stackForm.errors.security_groups && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.security_groups}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    App Port
                    <Tooltip
                      content="Port on which your application listens for traffic."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="app_port"
                    value={stackForm.values.app_port}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter port number"
                    min={1}
                    max={65535}
                    disabled={isCreating}
                  />
                  {stackForm.touched.app_port && stackForm.errors.app_port && (
                    <p className="text-xs text-red-500 mt-1">
                      {stackForm.errors.app_port}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    ASG Min Size
                    <Tooltip
                      content="Minimum number of instances"
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="asg_min_size"
                    value={stackForm.values.asg_min_size}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    min={0}
                    disabled={isCreating}
                  />
                  {stackForm.touched.asg_min_size &&
                    stackForm.errors.asg_min_size && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.asg_min_size}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    ASG Max Size
                    <Tooltip
                      content="Maximum number of instances"
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="asg_max_size"
                    value={stackForm.values.asg_max_size}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={Math.max(1, stackForm.values.asg_min_size || 1)}
                    disabled={isCreating}
                  />
                  {stackForm.touched.asg_max_size &&
                    stackForm.errors.asg_max_size && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.asg_max_size}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    ASG Desired Capacity
                    <Tooltip
                      content="Initial number of instances"
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="asg_desired_capacity"
                    value={
                      stackForm.values.asg_desired_capacity ||
                      stackForm.values.asg_min_size
                    }
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={stackForm.values.asg_min_size || 0}
                    max={stackForm.values.asg_max_size || 999}
                    disabled={isCreating}
                  />
                  {stackForm.touched.asg_desired_capacity &&
                    stackForm.errors.asg_desired_capacity && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.asg_desired_capacity}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    ASG Cooldown (seconds)
                    <Tooltip
                      content="Minimum wait time between any scaling actions for the Auto Scaling Group."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="asg_cooldown"
                    value={stackForm.values.asg_cooldown}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={0}
                    disabled={isCreating}
                  />
                  {stackForm.touched.asg_cooldown &&
                    stackForm.errors.asg_cooldown && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.asg_cooldown}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Scale Up Adjustment
                    <Tooltip
                      content="Number of instances to add when scaling up"
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="scale_up_adjustment"
                    value={stackForm.values.scale_up_adjustment}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={0}
                    disabled={isCreating}
                  />
                  {stackForm.touched.scale_up_adjustment &&
                    stackForm.errors.scale_up_adjustment && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.scale_up_adjustment}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Scale Down Adjustment
                    <Tooltip
                      content="Number of instances to remove when scaling down"
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="scale_down_adjustment"
                    value={stackForm.values.scale_down_adjustment}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={0}
                    disabled={isCreating}
                  />
                  {stackForm.touched.scale_down_adjustment &&
                    stackForm.errors.scale_down_adjustment && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.scale_down_adjustment}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Scaling Cooldown (seconds)
                    <Tooltip
                      content="Wait time before this scaling policy can run again after it scales."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="scaling_cooldown"
                    value={stackForm.values.scaling_cooldown}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    min={0}
                    disabled={isCreating}
                  />
                  {stackForm.touched.scaling_cooldown &&
                    stackForm.errors.scaling_cooldown && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.scaling_cooldown}
                      </p>
                    )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    CPU Scale Up Threshold (%)
                    <Tooltip
                      content="Set the CPU usage (%) at which the system should add more instances to handle increased load."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="cpu_scale_up_threshold_percent"
                    value={stackForm.values.cpu_scale_up_threshold_percent}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={1}
                    max={100}
                    disabled={isCreating}
                  />
                  {stackForm.touched.cpu_scale_up_threshold_percent &&
                    stackForm.errors.cpu_scale_up_threshold_percent && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.cpu_scale_up_threshold_percent}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    CPU Scale Down Threshold (%)
                    <Tooltip
                      content="Set the CPU usage (%) below which the system reduces the number of instances to save resources."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="cpu_scale_down_threshold_percent"
                    value={stackForm.values.cpu_scale_down_threshold_percent}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={1}
                    max={100}
                    disabled={isCreating}
                  />
                  {stackForm.touched.cpu_scale_down_threshold_percent &&
                    stackForm.errors.cpu_scale_down_threshold_percent && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.cpu_scale_down_threshold_percent}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    CPU Evaluation Periods
                    <Tooltip
                      content="Number of consecutive periods CPU metrics must breach the threshold before scaling is triggered."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="cpu_evaluation_periods"
                    value={stackForm.values.cpu_evaluation_periods}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={1}
                    disabled={isCreating}
                  />
                  {stackForm.touched.cpu_evaluation_periods &&
                    stackForm.errors.cpu_evaluation_periods && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.cpu_evaluation_periods}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    RAM Scale Up Threshold (%)
                    <Tooltip
                      content="Set the RAM usage (%) at which the system should add more instances to handle increased load."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="ram_scale_up_threshold_percent"
                    value={stackForm.values.ram_scale_up_threshold_percent}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={1}
                    max={100}
                    disabled={isCreating}
                  />
                  {stackForm.touched.ram_scale_up_threshold_percent &&
                    stackForm.errors.ram_scale_up_threshold_percent && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.ram_scale_up_threshold_percent}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    RAM Scale Down Threshold (%)
                    <Tooltip
                      content="Set the RAM usage (%) below which the system reduces the number of instances to save resources."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="ram_scale_down_threshold_percent"
                    value={stackForm.values.ram_scale_down_threshold_percent}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={1}
                    max={100}
                    disabled={isCreating}
                  />
                  {stackForm.touched.ram_scale_down_threshold_percent &&
                    stackForm.errors.ram_scale_down_threshold_percent && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.ram_scale_down_threshold_percent}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    RAM Evaluation Periods
                    <Tooltip
                      content="Number of consecutive time intervals that average RAM usage must exceed or fall below the defined threshold before a scaling action is triggered."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="ram_evaluation_periods"
                    value={stackForm.values.ram_evaluation_periods}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={1}
                    disabled={isCreating}
                  />
                  {stackForm.touched.ram_evaluation_periods &&
                    stackForm.errors.ram_evaluation_periods && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.ram_evaluation_periods}
                      </p>
                    )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Listener Protocol
                    <Tooltip
                      content="Protocol used by the load balancer to accept and route incoming traffic."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Select
                    name="lb_protocol"
                    value={stackForm.values.lb_protocol}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    disabled={isCreating}
                  >
                    <option value="">Select protocol</option>
                    <option value="HTTP">HTTP</option>
                    {/* <option value="HTTPS">HTTPS</option> */}
                    <option value="TERMINATED_HTTPS">HTTPS</option>
                    <option value="TCP">TCP</option>
                  </Select>
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    LB Protocol Port
                    <Tooltip
                      content={
                        stackForm.values.lb_protocol === 'HTTP'
                          ? 'Automatically set to port 80'
                          : stackForm.values.lb_protocol === 'HTTPS'
                            ? 'Automatically set to port 443'
                            : stackForm.values.lb_protocol ===
                                'TERMINATED_HTTPS'
                              ? 'Automatically set to port 443'
                              : stackForm.values.lb_protocol === 'TCP'
                                ? 'Enter the TCP port number'
                                : 'Port number for the load balancer protocol'
                      }
                      className="!bg-primaryBlue !text-white"
                      placement="left"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="lb_protocol_port"
                    value={stackForm.values.lb_protocol_port}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    disabled={
                      isCreating ||
                      stackForm.values.lb_protocol === 'HTTP' ||
                      stackForm.values.lb_protocol === 'HTTPS' ||
                      stackForm.values.lb_protocol === 'TERMINATED_HTTPS'
                    }
                    min={1}
                    max={65535}
                  />
                  {stackForm.touched.lb_protocol_port &&
                    stackForm.errors.lb_protocol_port && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.lb_protocol_port}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Pool Protocol
                    <Tooltip
                      content="Protocol used by the load balancer pool to communicate with backend instances. Default and only value is HTTP."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Select
                    name="pool_protocol"
                    value={stackForm.values.pool_protocol}
                    disabled={true}
                  >
                    <option value="HTTP">HTTP</option>
                  </Select>
                </div>
                {stackForm.values.lb_protocol === 'TERMINATED_HTTPS' && (
                  <div>
                    <label className="mb-1 text-sm flex items-center gap-2">
                      Certificates
                      <Tooltip
                        content="Select the certificate container to use for TLS termination."
                        className="!bg-primaryBlue !text-white"
                        placement="right"
                      >
                        <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                      </Tooltip>
                    </label>
                    <Select
                      name="default_tls_container_ref"
                      value={stackForm.values.default_tls_container_ref}
                      onChange={handleChange}
                      onBlur={stackForm.handleBlur}
                      disabled={isCreating}
                    >
                      {certificateOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                    {stackForm.touched.default_tls_container_ref &&
                      stackForm.errors.default_tls_container_ref && (
                        <p className="text-xs text-red-500 mt-1">
                          {stackForm.errors.default_tls_container_ref}
                        </p>
                      )}
                  </div>
                )}
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Load Balancer Algorithm
                    <Tooltip
                      content="Strategy used by the load balancer to distribute incoming traffic across healthy instances."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Select
                    name="lb_algorithm"
                    value={stackForm.values.lb_algorithm}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    disabled={isCreating}
                  >
                    <option value="">Select algorithm</option>
                    <option value="ROUND_ROBIN">ROUND ROBIN</option>
                    <option value="LEAST_CONNECTIONS">LEAST CONNECTIONS</option>
                    <option value="SOURCE_IP">SOURCE IP</option>
                  </Select>
                  {stackForm.touched.lb_algorithm &&
                    stackForm.errors.lb_algorithm && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.lb_algorithm}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Health Check Type
                    <Tooltip
                      content="Method used by the load balancer to determine whether an instance is healthy and able to receive traffic."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Select
                    name="health_check_type"
                    value={stackForm.values.health_check_type}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    disabled={isCreating}
                  >
                    <option value="">Select type</option>
                    <option value="HTTP">HTTP</option>
                    <option value="HTTPS">HTTPS</option>
                    <option value="TCP">TCP</option>
                    <option value="PING">PING</option>
                  </Select>
                  {stackForm.touched.health_check_type &&
                    stackForm.errors.health_check_type && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.health_check_type}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Health Check Delay (seconds)
                    <Tooltip
                      content="Time to wait after instance launch before starting health checks."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="health_check_delay"
                    value={stackForm.values.health_check_delay}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={0}
                    disabled={isCreating}
                  />
                  {stackForm.touched.health_check_delay &&
                    stackForm.errors.health_check_delay && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.health_check_delay}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Health Check Timeout (seconds)
                    <Tooltip
                      content="Maximum time to wait for a health check response."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="health_check_timeout"
                    value={stackForm.values.health_check_timeout}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={0}
                    disabled={isCreating}
                  />
                  {stackForm.touched.health_check_timeout &&
                    stackForm.errors.health_check_timeout && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.health_check_timeout}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Health Check Retries
                    <Tooltip
                      content="Number of consecutive failed health check attempts required before an instance is marked unhealthy."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="number"
                    name="health_check_max_retries"
                    value={stackForm.values.health_check_max_retries}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={stackForm.handleBlur}
                    min={1}
                    max={10}
                    disabled={isCreating}
                  />
                  {stackForm.touched.health_check_max_retries &&
                    stackForm.errors.health_check_max_retries && (
                      <p className="text-xs text-red-500 mt-1">
                        {stackForm.errors.health_check_max_retries}
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Health Check URL Path
                    <Tooltip
                      content="URL path that the load balancer uses to check instance health."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="text"
                    name="health_check_url_path"
                    value={stackForm.values.health_check_url_path}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    disabled={isCreating}
                  />
                </div>
                <div>
                  <label className="mb-1 text-sm flex items-center gap-2">
                    Health Check Expected Codes
                    <Tooltip
                      content="HTTP status codes that indicate a healthy instance."
                      className="!bg-primaryBlue !text-white"
                      placement="right"
                    >
                      <BsInfoCircle className="h-4 w-4 text-primaryBlue cursor-help hover:text-primaryBlue/80" />
                    </Tooltip>
                  </label>
                  <Input
                    type="text"
                    name="health_check_expected_codes"
                    value={stackForm.values.health_check_expected_codes}
                    onChange={handleChange}
                    onBlur={stackForm.handleBlur}
                    placeholder="e.g 200"
                    disabled={isCreating}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    stackForm.resetForm();
                    setIsOpen(false);
                  }}
                  className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className={`rounded bg-primaryBlue px-3 py-2 text-sm text-white hover:bg-blue-700 flex items-center gap-2 ${
                    isCreating ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isCreating
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                      ? 'Update'
                      : 'Create'}
                </button>
              </div>
            </form>
          )}
        </Drawer>
      </div>

      {/* Flavor Details Modal */}
      {showFlavorModal && selectedFlavorDetails && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-xl p-6 md:p-8 w-full md:w-1/2 max-w-2xl max-h-[90%] overflow-y-auto">
            <div className="flex items-center justify-between mb-6 border-b border-gray-200 dark:border-[#c4c4c432] pb-4">
              <h2 className="text-primaryBlue font-semibold text-lg">
                Flavor Details
              </h2>
              <button
                onClick={() => {
                  setShowFlavorModal(false);
                  setSelectedFlavorDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Name
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedFlavorDetails.name}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    VCPUs
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedFlavorDetails.vcpus ||
                      selectedFlavorDetails.vCPUs ||
                      'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    RAM
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedFlavorDetails.ram ||
                      selectedFlavorDetails.RAM ||
                      'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Disk/Storage
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedFlavorDetails.disk ||
                      selectedFlavorDetails.Disk ||
                      'N/A'}
                  </p>
                </div>
              </div>

              {selectedFlavorDetails.description && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Description
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedFlavorDetails.description}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-[#c4c4c432]">
              <button
                onClick={() => {
                  setShowFlavorModal(false);
                  setSelectedFlavorDetails(null);
                }}
                className="bg-[#1DA1F2] hover:bg-[#1a91da] text-white text-sm px-6 py-2 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AutoScaling;
